/**
 * Drop-in replacements for next/navigation hooks that work during
 * static prerendering in the pages directory.
 *
 * During static prerendering, useRouter throws
 * "NextRouter was not mounted". We catch this and return
 * a safe fallback router object instead.
 */
import React from "react";

const noop = () => {};

const FALLBACK_ROUTER = {
  push: noop,
  replace: noop,
  back: noop,
  forward: noop,
  reload: noop,
  prefetch: () => Promise.resolve(),
  query: {},
  locale: undefined,
  pathname: "/",
  asPath: "/",
  isReady: false,
  route: "/",
  basePath: "",
  events: { on: noop, off: noop, emit: noop },
};

// next/dist/shared/lib/router-context.shared-runtime is the stable path
// for Next.js 13+. Using require() so Turbopack only warns (not errors)
// if the path ever changes, rather than hard-failing the build.
let RouterContext = null;
try {
  RouterContext =
    require("next/dist/shared/lib/router-context.shared-runtime").RouterContext;
} catch {
  // RouterContext unavailable; useSafeRouter will return FALLBACK_ROUTER
}

function useSafeRouter() {
  if (RouterContext) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const router = React.useContext(RouterContext);
    if (router) return router;
  }
  return FALLBACK_ROUTER;
}

export function useRouter() {
  const router = useSafeRouter();
  return {
    push: router.push || noop,
    replace: router.replace || noop,
    back: router.back || noop,
    forward: () =>
      typeof window !== "undefined" ? window.history.forward() : undefined,
    refresh: router.reload || noop,
    prefetch: router.prefetch || (() => Promise.resolve()),
    query: router.query || {},
    locale: router.locale,
    pathname: router.pathname || "/",
    asPath: router.asPath || "/",
    isReady: router.isReady || false,
  };
}

export function usePathname() {
  const router = useSafeRouter();
  return (router.asPath || "/").split("?")[0];
}

export function useParams() {
  const router = useSafeRouter();
  return router.query || {};
}

export function useSearchParams() {
  const router = useSafeRouter();
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams((router.asPath || "").split("?")[1] || "");
}
